import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, Calendar, TrendingUp, Car, IndianRupee } from "lucide-react";
import { Navigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import EarningsTab from "@/components/owner/EarningsTab";
import ArrivalsTab from "@/components/owner/ArrivalsTab";
import BlackoutsTab from "@/components/owner/BlackoutsTab";
import SpotsManagerTab from "@/components/owner/SpotsManagerTab";
import NotificationsTab from "@/components/owner/NotificationsTab";
import InsightsTab from "@/components/owner/InsightsTab";
import PrintListTab from "@/components/owner/PrintListTab";

interface Stats {
  todayBookings: number;
  weekEarnings: number;
  totalSpots: number;
  availableSpots: number;
}

const OwnerDashboard = () => {
  const [authReady, setAuthReady] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({ todayBookings: 0, weekEarnings: 0, totalSpots: 0, availableSpots: 0 });

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
      setAuthReady(true);
    });
  }, []);

  useEffect(() => {
    if (!authReady || !userId) return;
    (async () => {
      const { data: spots } = await supabase
        .from("parking_spots")
        .select("id, is_available, price_per_hour")
        .eq("owner_id", userId);
      const ids = (spots ?? []).map((s) => s.id);
      let todayBookings = 0;
      let weekEarnings = 0;
      if (ids.length) {
        const now = new Date();
        const sod = new Date(now); sod.setHours(0, 0, 0, 0);
        const eod = new Date(now); eod.setHours(23, 59, 59, 999);
        const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate() - 7);
        const priceMap = new Map((spots ?? []).map((s) => [s.id, Number(s.price_per_hour)]));
        const { data: bk } = await supabase
          .from("bookings")
          .select("start_time, end_time, status, parking_spot_id, created_at")
          .in("parking_spot_id", ids);
        (bk ?? []).forEach((b) => {
          const st = new Date(b.start_time);
          if (st >= sod && st <= eod) todayBookings++;
          if (b.status === "completed" && new Date(b.created_at) >= weekAgo) {
            const hrs = Math.max(1, Math.round((new Date(b.end_time).getTime() - st.getTime()) / 3.6e6));
            weekEarnings += hrs * (priceMap.get(b.parking_spot_id) ?? 0);
          }
        });
      }
      setStats({
        todayBookings,
        weekEarnings,
        totalSpots: spots?.length ?? 0,
        availableSpots: (spots ?? []).filter((s) => s.is_available).length,
      });
      setLoading(false);
    })();
  }, [authReady, userId]);

  if (authReady && !userId) return <Navigate to="/login" />;
  if (!authReady || loading || !userId)
    return (
      <div className="container mx-auto px-4 py-16 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );

  const occupancy = stats.totalSpots > 0
    ? Math.round(((stats.totalSpots - stats.availableSpots) / stats.totalSpots) * 100)
    : 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">Owner Dashboard</h1>
      <p className="text-muted-foreground mb-6">Manage your listings, bookings and earnings</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card><CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div><CardTitle className="text-2xl">{stats.todayBookings}</CardTitle><CardDescription>Today</CardDescription></div>
          <Calendar className="h-5 w-5 text-primary" />
        </CardHeader></Card>
        <Card><CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div><CardTitle className="text-2xl">{occupancy}%</CardTitle><CardDescription>Occupancy</CardDescription></div>
          <TrendingUp className="h-5 w-5 text-emerald-500" />
        </CardHeader></Card>
        <Card><CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div><CardTitle className="text-2xl">{stats.totalSpots}</CardTitle><CardDescription>Listings</CardDescription></div>
          <Car className="h-5 w-5 text-blue-500" />
        </CardHeader></Card>
        <Card><CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div><CardTitle className="text-2xl">₹{stats.weekEarnings.toFixed(0)}</CardTitle><CardDescription>7-day Earnings</CardDescription></div>
          <IndianRupee className="h-5 w-5 text-indigo-500" />
        </CardHeader></Card>
      </div>

      <Tabs defaultValue="arrivals" className="w-full">
        <TabsList className="w-full justify-start mb-6 overflow-x-auto flex-nowrap">
          <TabsTrigger value="arrivals">Arrivals</TabsTrigger>
          <TabsTrigger value="earnings">Earnings</TabsTrigger>
          <TabsTrigger value="spots">Spots</TabsTrigger>
          <TabsTrigger value="blackouts">Calendar</TabsTrigger>
          <TabsTrigger value="insights">Insights</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="print">Print</TabsTrigger>
        </TabsList>
        <TabsContent value="arrivals"><ArrivalsTab ownerId={userId} /></TabsContent>
        <TabsContent value="earnings"><EarningsTab ownerId={userId} /></TabsContent>
        <TabsContent value="spots"><SpotsManagerTab ownerId={userId} /></TabsContent>
        <TabsContent value="blackouts"><BlackoutsTab ownerId={userId} /></TabsContent>
        <TabsContent value="insights"><InsightsTab ownerId={userId} /></TabsContent>
        <TabsContent value="notifications"><NotificationsTab ownerId={userId} /></TabsContent>
        <TabsContent value="print"><PrintListTab ownerId={userId} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default OwnerDashboard;
